import { Node, mergeAttributes } from '@tiptap/react';

export interface PlaceholderOptions {
    HTMLAttributes: Record<string, string>;
}

declare module '@tiptap/react' {
    interface Commands<ReturnType> {
        placeholder: {
            insertPlaceholder: (token: string, mandatory: boolean) => ReturnType;
        };
    }
}

export const PlaceholderExtension = Node.create<PlaceholderOptions>({
    name: 'placeholder',

    group: 'inline',

    inline: true,

    selectable: true,

    atom: true,

    addAttributes() {
        return {
            token: {
                default: null,
                parseHTML: (element) => element.getAttribute('data-token'),
                renderHTML: (attributes) => {
                    if (!attributes.token) {
                        return {};
                    }
                    return {
                        'data-token': attributes.token,
                    };
                },
            },
            mandatory: {
                default: false,
                parseHTML: (element) => element.getAttribute('data-mandatory') === 'true',
                renderHTML: (attributes) => {
                    return {
                        'data-mandatory': attributes.mandatory,
                    };
                },
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-type="placeholder"]',
            },
        ];
    },

    renderHTML({ node, HTMLAttributes }) {
        const token = typeof node.attrs.token === 'string' ? node.attrs.token : '';
        const mandatory = node.attrs.mandatory === true || node.attrs.mandatory === 'true';
        const classes = mandatory
            ? 'inline-flex items-center gap-1 px-2 py-0.5 rounded text-sm font-mono bg-primary/10 text-primary border border-primary/30'
            : 'inline-flex items-center gap-1 px-2 py-0.5 rounded text-sm font-mono bg-muted text-muted-foreground border border-border';

        return [
            'span',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'placeholder',
                'data-token': token,
                'data-mandatory': mandatory ? 'true' : 'false',
                class: classes,
                contenteditable: 'false',
                style: mandatory
                    ? 'display:inline-flex;align-items:center;padding:1px 6px;border-radius:4px;font-family:ui-monospace,monospace;font-size:13px;background:#ede9fe;color:#6d28d9;border:1px solid #c4b5fd;'
                    : 'display:inline-flex;align-items:center;padding:1px 6px;border-radius:4px;font-family:ui-monospace,monospace;font-size:13px;background:#f3f4f6;color:#374151;border:1px solid #d1d5db;',
            }),
            token,
        ];
    },

    renderText({ node }) {
        return typeof node.attrs.token === 'string' ? node.attrs.token : '';
    },

    addCommands() {
        return {
            insertPlaceholder:
                (token: string, mandatory: boolean) =>
                ({ commands }) => {
                    return commands.insertContent({
                        type: this.name,
                        attrs: {
                            token,
                            mandatory,
                        },
                    });
                },
        };
    },
});
